import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, finalize, map, Observable, of, startWith, Subscription } from 'rxjs';
import { ConfigurationService } from '../../../service/configuration/configuration.service';
import { FeatureFlags } from '../../../core/util/resources';
import { FormElements, FormKey, searchNotificationMessages } from '../../../model/common-elements';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { saveKeyValue } from '../../../store/common/common-actions';
import { selectKeyValuePairs } from '../../../store/common/common-selectors';
import { UppComponentNames, UppViewNames, ViewMode } from '../../../service/model';
import { UppComponent } from '../../../service/model/upp-component';
import {
  NAME_WITH_SPACES_VALIDATION_MESSAGES,
  TWO_SYMBOLS_REQUIRED
} from '../../../service/model/common/validation-messages';
import { alphanumericSpaceMax30, twoAlphaNumericRegex } from '../../../service/model/common/validators';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType,
  SearchResult
} from '../../../base/search/basic-search-result-table/basic-search-result-table.component';
import { AirFamily } from '../model/air-family';
import { AirFamilyDeleteRequest, AirFamilySearchRequest } from '../model/air-family-request';
import { PERMISSION_MANAGE_FAMILY } from '../../../service/user-permissions.service';
import { AirFamiliesService } from '../../../service/air-families.service';
import { AgGridSearchTableComponent } from '../../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { TableActionsComponent } from '../../../components/table-actions/table-actions.component';
import { NavigationService } from '../../../service/core/navigation.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
  airFamilyDeleted,
  airFamilyNotificationClosed,
  clearAirFamilyNamesSelection,
  saveAirFamilies,
  saveAirFamily,
  setNotificationData
} from '../../../store/families/air/air-families-action';
import {
  selectAirFamilies,
  selectAirFamily,
  selectAirFamilyNotification,
  selectSelectedAirFamilies,
  selectAirFamilyCurrentPage,
  selectAirFamilyPageSize,
  selectAirFamilyTotalCount
} from '../../../store/families/air/air-families-selector';
import { selectNavigation } from '../../../store/navigation/navigation-selector';
import { Router } from '@angular/router';
import { RouteURL } from '../../../model/route-url';
import { UserDetailsService } from '../../../service/user-details.service';
import { UppNotification, UppNotificationStatus } from '../../../model/notification';
import { AirFamilySearchResponse } from '../model/air-family-response';
import { NotificationService } from '../../../service/notification.service';
import { NgClass, AsyncPipe } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LoadingAlertComponent } from '../../../components/loading-alert/loading-alert.component';
import { LookupSelectComponent } from '../../../components/lookup-select/lookup-select.component';
import { UppNotificationComponent } from '../../../components/upp-notification/upp-notification.component';
import { ValidationErrorMessageComponent } from '../../../components/validation-error-message/validation-error-message.component';

export interface AirFamiliesSearchFormValue {
  name?: string;
  carrierCodes?: string[];
}

@Component({
  selector: 'app-air-families-search',
  templateUrl: './air-families-search.component.html',
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    ReactiveFormsModule,
    NgbTooltip,
    NgClass,
    ValidationErrorMessageComponent,
    LookupSelectComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class AirFamiliesSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  private readonly _isLoadingSubject = new BehaviorSubject<boolean>(false);

  get isLoading(): boolean {
    return this._isLoadingSubject.value;
  }

  set isLoading(value: boolean) {
    this._isLoadingSubject.next(value);
  }

  searchNotificationMessages = searchNotificationMessages;

  NAME_WITH_SPACES_VALIDATION_MESSAGES = NAME_WITH_SPACES_VALIDATION_MESSAGES;
  TWO_SYMBOLS_REQUIRED = TWO_SYMBOLS_REQUIRED;
  PERMISSION_MANAGE_FAMILY = PERMISSION_MANAGE_FAMILY;

  UppComponentNames = UppComponentNames;
  UppViewNames = UppViewNames;
  EntryType = EntryType;

  airFamiliesSearchForm!: FormGroup;
  FormElements = FormElements;
  airFamilies$!: Observable<AirFamily[]>;
  transformedAirFamilies$!: Observable<AirFamily[]>;

  currentPage$!: Observable<number>;
  pageSize$!: Observable<number>;
  totalCount$!: Observable<number>;

  currentPage = 0;
  pageSize = 10;

  selectedAirFamilyNames$: Observable<Set<string>>;
  notification$!: Observable<UppNotification | undefined>;

  displayableFields: DisplayableField[] = [
    {
      value: 'description',
      label: 'Description'
    }
  ];

  uppComponent = UppComponent.FAMILIES_SEARCH;
  managePermission = PERMISSION_MANAGE_FAMILY;

  agGridTablesActivated = false;
  paginationEnabled = false;
  partialNameMatchEnabled = false;

  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);

  private readonly subscription = new Subscription();

  constructor(
    store: Store,
    router: Router,
    navigationService: NavigationService,
    readonly configurationService: ConfigurationService,
    private readonly formBuilder: FormBuilder,
    private readonly airFamiliesService: AirFamiliesService,
    private readonly userDetailsService: UserDetailsService,
    private readonly notificationService: NotificationService
  ) {
    super(store, router, navigationService);

    this.configurationService = configurationService;
    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
        this.agGridTablesActivated = isEnabled ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.partialNameMatch).subscribe((isEnabled) => {
        this.partialNameMatchEnabled = isEnabled ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.pagination).subscribe((isEnabled) => {
        this.paginationEnabled = isEnabled ?? false;
      })
    );

    this.selectedAirFamilyNames$ = this.store.pipe(
      select(selectSelectedAirFamilies),
      filter((selectedFamilies): selectedFamilies is Set<string> => !!selectedFamilies),
      startWith(new Set<string>())
    );

    this.currentPage$ = this.store.pipe(select(selectAirFamilyCurrentPage));
    this.pageSize$ = this.store.pipe(select(selectAirFamilyPageSize));
    this.totalCount$ = this.store.pipe(select(selectAirFamilyTotalCount));

    this.transformedAirFamilies$ = combineLatest([
      this.store.pipe(select(selectAirFamilies)),
      this.currentPage$,
      this.pageSize$,
      this.totalCount$,
      this._isLoadingSubject
    ]).pipe(
      map(([airFamilies, currentPage, pageSize, totalCount, isLoading]) => {
        const filtered = (airFamilies || []).filter((af) => !af?.statusType);
        if (this.paginationEnabled && totalCount > 0) {
          const fullArray = Array.from({ length: totalCount }, (_, i) => ({ __idx: i })) as (
            | AirFamily
            | { __idx: number }
          )[];
          if (!isLoading && filtered.length > 0) {
            const startIndex = currentPage * pageSize;
            filtered.forEach((af, index) => {
              fullArray[startIndex + index] = af;
            });
          }
          return fullArray as AirFamily[];
        }
        return filtered;
      })
    );
  }

  get nameControl(): AbstractControl | null {
    return this.airFamiliesSearchForm.get(FormElements.NAME);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.createForm();
    this.patchForm();
    this.getAirFamilies();
    this.notification$ = this.store.select(selectAirFamilyNotification(UppViewNames.SEARCH));

    this.store.dispatch(clearAirFamilyNamesSelection());

    if (this.agGridTablesActivated) {
      this.initializeColumnDefs();
    }

    this.subscription.add(
      this.currentPage$.subscribe((page) => {
        this.currentPage = page;
      })
    );

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        this.pageSize = size;
      })
    );

    this.subscription.add(
      this.store.pipe(select(selectNavigation)).subscribe((lookupParam) => {
        if (lookupParam) {
          this.lookupOptions = lookupParam;
          this.lookupValues = lookupParam.data || [];
        }
      })
    );

    this.lookup = !!this.lookupOptions?.lookup;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.store.dispatch(
      saveKeyValue({
        key: FormKey.AirFamiliesSearch,
        value: this.airFamiliesSearchForm.getRawValue()
      })
    );
    this.subscription.unsubscribe();
  }

  onSearchSubmit(): void {
    this.currentPage = 0;
    this.search();
    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  search(): void {
    this.isLoading = true;
    const request = this.prepareSearchRequest(this.airFamiliesSearchForm.getRawValue());

    this.subscription.add(
      this.airFamiliesService
        .search(request)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe((response: AirFamilySearchResponse) => {
          this.notificationService.handleResponse(response, UppViewNames.SEARCH);
          if (response.status.state === UppNotificationStatus.SUCCESS) {
            const pagination = response.searchFollowUpResponse
              ? {
                  totalCount: response.searchFollowUpResponse.totalRecords,
                  currentPage: response.searchFollowUpResponse.currentPageNumber - 1,
                  pageSize: this.pageSize
                }
              : undefined;
            this.store.dispatch(saveAirFamilies({ airFamilies: response.carriersFamilies ?? [], pagination }));
          }
          if (response.status.state === UppNotificationStatus.WARNING) {
            this.store.dispatch(saveAirFamilies({ airFamilies: [] }));
          }

          if (this.agGridTablesActivated) {
            this.getAirFamilies();
          }
        })
    );
  }

  prepareSearchRequest(formValue: AirFamiliesSearchFormValue): AirFamilySearchRequest {
    const request: AirFamilySearchRequest = {
      version: '2.0',
      carriersFamily: {}
    };

    if (this.partialNameMatchEnabled) {
      request.carriersFamily.enableFullTextSearch = true;
    }

    if (formValue.name) {
      request.carriersFamily.name = formValue.name;
    }

    if (formValue.carrierCodes?.length) {
      request.carriersFamily.includedCarriers = formValue.carrierCodes;
    }

    request.carriersFamily = this.userDetailsService.assignEntity(request.carriersFamily);

    if (this.paginationEnabled) {
      request.searchFollowUpRequest = {
        requestedPageNumber: String(this.currentPage + 1),
        numberOfRecords: String(this.pageSize)
      };
    }

    return request;
  }

  clearForm(): void {
    this.airFamiliesSearchForm.reset();
    this.store.dispatch(
      saveKeyValue({
        key: FormKey.AirFamiliesSearch,
        value: null
      })
    );
    this.clearAirFamilies();
  }

  recordToSearchResult(airFamily: AirFamily): SearchResult {
    return {
      id: airFamily.id,
      name: airFamily.name,
      description: airFamily.description,
      version: airFamily.version
    };
  }

  entryToUpdateNameResolver(deleteRequest: AirFamilyDeleteRequest): string | undefined {
    return deleteRequest.airFamily.name;
  }

  deleteAirFamily(deleteRequest: AirFamilyDeleteRequest): void {
    this.subscription.add(
      this.airFamiliesService.delete(deleteRequest).subscribe((response) => {
        this.notificationService.handleResponse(response, UppViewNames.SEARCH);
        if (response.status.state === UppNotificationStatus.SUCCESS) {
          this.store.dispatch(airFamilyDeleted({ id: deleteRequest.airFamily.id }));
        }
      })
    );
  }

  clearAirFamilies(): void {
    this.store.dispatch(
      saveAirFamilies({
        airFamilies: [],
        pagination: { totalCount: 0, currentPage: 0, pageSize: this.pageSize }
      })
    );

    if (this.agGridTablesActivated) {
      this.airFamilies$ = of([]);
      this.lookupValues = [];
    }
  }

  closeNotification(): void {
    this.store.dispatch(airFamilyNotificationClosed({ viewMode: UppViewNames.SEARCH }));
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    _organization: string,
    version: string,
    ruleVersion: number
  ): AirFamilyDeleteRequest {
    return {
      version,
      airFamily: {
        id,
        name,
        version: ruleVersion
      }
    };
  }

  showAirFamily(id: string, viewMode: ViewMode): void {
    this.subscription.add(
      this.store.select(selectAirFamily(id)).subscribe((airFamily) => {
        if (airFamily) {
          const airFamilyCopy = { ...airFamily };
          delete airFamilyCopy.statusNotification;

          // Save the selected air family in the store for display and modify view
          this.store.dispatch(
            setNotificationData({
              notification: { warning: [], error: [], success: [] },
              viewMode: UppViewNames.DISPLAY
            })
          );
          this.store.dispatch(saveAirFamily({ selectedAirFamily: airFamilyCopy, viewMode: UppViewNames.MODIFY }));
          this.store.dispatch(saveAirFamily({ selectedAirFamily: airFamilyCopy, viewMode: UppViewNames.DISPLAY }));

          this.store.dispatch(
            saveKeyValue({
              key: `${FormKey.AirFamiliesSearch}`,
              value: null
            })
          );
        }
        // Clear the form to avoid showing the previously saved form data
        this.store.dispatch(
          saveKeyValue({
            key: `${FormKey.AirFamilyForm}-${UppViewNames.MODIFY}`,
            value: null
          })
        );

        this.store.dispatch(
          saveKeyValue({
            key: `${FormKey.AirFamilyForm}-${UppViewNames.DISPLAY}`,
            value: null
          })
        );

        const path = viewMode === UppViewNames.MODIFY ? RouteURL.familyAirModify : RouteURL.familyAirDisplay;
        this.router.navigate([path]);
      })
    );
  }

  private createForm(): void {
    this.airFamiliesSearchForm = this.formBuilder.group({
      [FormElements.NAME]: ['', [Validators.maxLength(30), Validators.pattern(alphanumericSpaceMax30)]],
      [FormElements.CARRIER_CODES]: ['', Validators.pattern(twoAlphaNumericRegex)]
    });
  }

  private patchForm(): void {
    this.subscription.add(
      this.store.select(selectKeyValuePairs).subscribe((kvp) => {
        const savedForm = kvp[FormKey.AirFamiliesSearch];
        if (savedForm) {
          this.airFamiliesSearchForm.patchValue(savedForm);
          this.airFamiliesSearchForm.markAllAsTouched();
        }
      })
    );
  }

  private getAirFamilies(): void {
    this.airFamilies$ = this.store.pipe(
      select(selectAirFamilies),
      map((airFamilies) => (airFamilies || []).filter((af) => !af?.statusType))
    );
  }
  private initializeColumnDefs(): void {
    const nameColDef: ColDef = {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      unSortIcon: true,
      resizable: false,
      suppressMovable: true,
      autoHeight: true
    };

    this.columnDefs = [
      nameColDef,
      {
        field: 'description',
        headerName: 'Description',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        minWidth: 250,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          copyForbidden: true
        }
      }
    ];

    this.gridOptions = {
      ...this.gridOptions,
      getRowId: (params) => params.data.id ?? `__ph_${params.data.__idx}`,
      getRowClass: (params) => (params.data?.id ? '' : 'ag-row-hidden'),
      postSortRows: (params) => {
        this.handlePostSortRowsForPagination(params, this.paginationEnabled);
      },
      onPaginationChanged: () => {
        if (!this.paginationEnabled || !this.gridApi || this.airFamiliesSearchForm.invalid) {
          return;
        }

        const newPage = this.gridApi.paginationGetCurrentPage();
        const newPageSize = this.gridApi.paginationGetPageSize();

        if (newPage !== this.currentPage || newPageSize !== this.pageSize) {
          this.currentPage = newPage;
          this.pageSize = newPageSize;
          this.search();
        }
      }
    };
  }

  override onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        if (!this.gridApi) {
          return;
        }
        if (size !== this.gridApi.paginationGetPageSize()) {
          this.gridApi.setGridOption('paginationPageSize', size);
        }
      })
    );
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.AIR_FAMILIES;
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const airFamily = data as AirFamily;
      if (airFamily.id) {
        this.showAirFamily(airFamily.id, UppViewNames.DISPLAY);
      }
    };
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const airFamily = data as AirFamily;
      if (airFamily.id) {
        this.showAirFamily(airFamily.id, UppViewNames.MODIFY);
      }
    };
  }

  getCopyHandler(): (data: unknown) => void {
    return () => {}; // Copy is forbidden for families
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const airFamily = data as AirFamily;
      if (airFamily.id) {
        const deleteRequest = this.entryToUpdateCreator(
          airFamily.id,
          airFamily.name,
          this.userDetailsService.loggedInUser?.organization || '',
          '1.0',
          airFamily.version || 1
        );
        this.deleteAirFamily(deleteRequest);
      }
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return () => {};
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return of(this.isLoading);
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId);
  }
}
